// ── ReferralSourceForm ──
// Reference record (dealflow origin). Never converted.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, FInput, FSelect, Field, Modal, ReadOnlyForm } from "../../../shared/ui";
import { useForm } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { DuplicateCheck } from "../components/DuplicateCheck";

const opts = (arr) => arr.map(v => ({ value: v, label: v }));
const RATING = opts(["", "A", "B", "C", "D"]);
const STATUS = opts(["Active", "Inactive"]);
const CTYPE = opts(["", "Lender", "Broker", "Attorney", "Accountant", "Referral Partner", "Other"]);
const CSUBTYPE = opts(["", "Bank", "Fund", "Private Lender", "Family Office", "Other"]);
const PHONE_TYPE = opts(["", "Work", "Mobile", "Home", "Other"]);
// Shared with the Referral Sources grid edit, so the two never offer different
// picklists for the same column.
export const RS_OPTIONS = { RATING, STATUS, CTYPE, CSUBTYPE };

// prefill seeds a NEW record; initial IS an existing one. Kept apart on
// purpose — passing a seed as `initial` would flip the title to "Edit", save
// it as an edit and hide the duplicate check, all of which would be lies about
// a record that does not exist yet. Email Review uses prefill.
export const ReferralSourceForm = ({ initial, readOnly, prefill, hint }) => {
  const { api, role, modal, setModal, closeModal, saving, error, saveRec } = useApp();
  const blank = {
    rating: "", company_name: "", title: "", firstname: "", lastname: "",
    contact_type: "", contact_subtype: "", region: "",
    street: "", city: "", state: "", zipcode: "", country: "",
    email: "", phone1_type: "Work", phone1: "", phone2_type: "Mobile", phone2: "", website: "",
    status: "Active", owner: "", notes: "",
  };
  const [f, set] = useForm(initial || { ...blank, ...(prefill || {}) });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("getUsers").then(r => setUsers(Array.isArray(r) ? r : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userOpts = [{ value: "", label: "— Select —" }, ...users.map(u => ({ value: u.id, label: u.FullName }))];
  const fullName = [f.firstname, f.lastname].filter(Boolean).join(" ");

  const missing = [];
  if (!f.company_name && !(f.firstname || f.lastname)) missing.push("Company or contact name");
  const validationError = missing.length ? "Required: " + missing.join(", ") : null;

  const ST = ({ title }) => <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, gridColumn:"1/-1" }}>{title}</div>;

  return (
    <Modal wide title={readOnly ? "View Referral Source" : initial ? "Edit Referral Source" : "Add Referral Source"} onClose={closeModal}
      onEdit={readOnly && role.canEdit ? () => { closeModal(); setModal({ type:"referralsource-edit", rec:initial, onDone:modal.onDone }); } : null}>
      <ReadOnlyForm readOnly={readOnly}>
        {/* Referral sources are scoped to their owner, so a rep cannot see one
            another rep cultivated — which would mean creating duplicates
            forever. This reports that it exists, who has it and how it is
            rated, and nothing else. New sources only; on an edit you already
            have the record. */}
        {!readOnly && hint && (
          /* Brand amber from shared/theme.js, not the raw hexes I first wrote.
             Those were a near-match by eye and would have stopped matching the
             moment the CSS variables behind B.amber* were retuned — which is
             the entire reason the tokens exist. */
          <div style={{ fontSize:12, color:B.amberDark, background:B.amberLight,
                        border:"1px solid " + B.amberMid,
                        borderRadius:6, padding:"8px 10px", marginBottom:10, lineHeight:1.5 }}>
            {hint}
          </div>
        )}
        {!readOnly && !initial && (
          <DuplicateCheck company={f.company_name || [f.firstname, f.lastname].filter(Boolean).join(" ")}
                          email={f.email} />
        )}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <ST title="Classification" />
          <Field label="Rating"><FSelect value={f.rating} onChange={set("rating")} options={RATING} /></Field>
          <Field label="Referral Source Status"><FSelect value={f.status} onChange={set("status")} options={STATUS} /></Field>
          <Field label="Owner">
            {role.isAdmin
              ? <FSelect value={f.owner} onChange={set("owner")} options={userOpts} />
              : <div style={{ padding:"7px 0", fontSize:13 }}>
                  {(users.find(u => u.id === f.owner) || {}).FullName
                    || (initial ? "—" : "You (on save)")}
                  <span style={{ color:"var(--color-text-secondary)", fontSize:11, marginLeft:8 }}>
                    admin can reassign
                  </span>
                </div>}
          </Field>
          <Field label="Contact Type"><FSelect value={f.contact_type} onChange={set("contact_type")} options={CTYPE} /></Field>
          <Field label="Contact Sub-Type"><FSelect value={f.contact_subtype} onChange={set("contact_subtype")} options={CSUBTYPE} /></Field>
          <Field label="Region"><FInput value={f.region} onChange={set("region")} /></Field>
          <div />

          <ST title="Contact" />
          <Field label="Company Name"><FInput value={f.company_name} onChange={set("company_name")} /></Field>
          <Field label="Title"><FInput value={f.title} onChange={set("title")} /></Field>
          <Field label="First Name"><FInput value={f.firstname} onChange={set("firstname")} /></Field>
          <Field label="Last Name"><FInput value={f.lastname} onChange={set("lastname")} /></Field>
          <Field label="Full Name"><div style={{ padding:"7px 0", fontSize:13, color:"var(--color-text-secondary)" }}>{fullName || "—"}</div></Field>
          <div />

          <ST title="Contact Info" />
          <Field label="E-Mail"><FInput value={f.email} onChange={set("email")} /></Field>
          <Field label="Website"><FInput value={f.website} onChange={set("website")} /></Field>
          <Field label="Phone 1 Type"><FSelect value={f.phone1_type} onChange={set("phone1_type")} options={PHONE_TYPE} /></Field>
          <Field label="Phone 1"><FInput value={f.phone1} onChange={set("phone1")} /></Field>
          <Field label="Phone 2 Type"><FSelect value={f.phone2_type} onChange={set("phone2_type")} options={PHONE_TYPE} /></Field>
          <Field label="Phone 2"><FInput value={f.phone2} onChange={set("phone2")} /></Field>

          <ST title="Address" />
          <Field label="Street"><FInput value={f.street} onChange={set("street")} /></Field>
          <Field label="City"><FInput value={f.city} onChange={set("city")} /></Field>
          <Field label="State"><FInput value={f.state} onChange={set("state")} /></Field>
          <Field label="Zip"><FInput value={f.zipcode} onChange={set("zipcode")} /></Field>
          <Field label="Country"><FInput value={f.country} onChange={set("country")} /></Field>
          <div />

          <ST title="Notes" />
          <div style={{ gridColumn:"1/-1" }}>
            <Field label="Follow-up Notes"><FInput value={f.notes || ""} onChange={set("notes")} /></Field>
          </div>
        </div>
      </ReadOnlyForm>

      {!readOnly && validationError && <div style={{ color:"#993C1D", fontSize:12, marginTop:10 }}>{validationError}</div>}
      {!readOnly && error && <div style={{ color:"#993C1D", fontSize:12, marginTop:8 }}>{error}</div>}

      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:12 }}>
        <Btn onClick={closeModal}>{readOnly ? "Close" : "Cancel"}</Btn>
        {!readOnly && <Btn disabled={saving || !!validationError}
          onClick={() => saveRec("saveReferralSource", { ...f }, initial ? "Edited" : "Added", "ReferralSources", (initial ? "Updated" : "Added") + " referral source", modal.onDone)}
          variant="primary">{saving ? "Saving..." : "Save"}</Btn>}
      </div>
    </Modal>
  );
};
